import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Stack, Typography } from '@mui/material';
import type { DashboardOverviewAnalyticsResponse } from '@/lib/api/dashboard';

import { DashboardAnalyticsOverviewCards } from './DashboardAnalyticsOverviewCards';
import { DashboardAnalyticsRecentPaymentsCard } from './DashboardAnalyticsRecentPaymentsCard';
import { DashboardAnalyticsRevenuePanel } from './DashboardAnalyticsRevenuePanel';
import { DashboardAnalyticsClientActivityCard } from './DashboardAnalyticsClientActivityCard';
import { DashboardAnalyticsQuestionnaireCard } from './DashboardAnalyticsQuestionnaireCard';
import { DashboardGettingStartedPanel } from './DashboardGettingStartedPanel';

export interface DashboardAnalyticsTabProps {
  data?: DashboardOverviewAnalyticsResponse;
  isLoading?: boolean;
  isPending?: boolean;
  error?: string;
  showGettingStarted?: boolean;
  onAddFirstClient: () => void;
  onSetAvailability: () => void;
  onSetupBilling: () => void;
  dateRangeLabel?: string;
  isCustomDateRange?: boolean;
}

export const DashboardAnalyticsTab: React.FC<DashboardAnalyticsTabProps> = ({
  data,
  isLoading = false,
  isPending = false,
  error,
  showGettingStarted = false,
  onAddFirstClient,
  onSetAvailability,
  onSetupBilling,
  dateRangeLabel,
  isCustomDateRange = false,
}) => {
  const navigate = useNavigate();
  const statusMessage = error || (isPending ? 'Dashboard analytics are being prepared. Please check again shortly.' : undefined);
  const invoicingDateBasisQuery = 'dateBasis=paidDate';

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      {statusMessage && (
        <Paper
          elevation={0}
          sx={{ border: '1px solid #e5e7eb', borderRadius: '12px', px: 2, py: 1.25, bgcolor: '#fbfcff' }}
        >
          <Typography sx={{ fontSize: 12, color: '#4a5565' }}>{statusMessage}</Typography>
        </Paper>
      )}
      {showGettingStarted && (
        <DashboardGettingStartedPanel
          onAddClient={onAddFirstClient}
          onSetAvailability={onSetAvailability}
          onSetupBilling={onSetupBilling}
        />
      )}
      {isCustomDateRange && dateRangeLabel && (
        <Paper
          elevation={0}
          sx={{ border: '1px solid #d9edf7', borderRadius: '12px', px: 2, py: 1.25, bgcolor: '#f7fcff' }}
        >
          <Typography sx={{ fontSize: 12, color: '#175b73' }}>Showing analytics for {dateRangeLabel}</Typography>
        </Paper>
      )}
      <DashboardAnalyticsOverviewCards data={data} isLoading={isLoading} />

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} useFlexGap sx={{ alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
          <DashboardAnalyticsRecentPaymentsCard
            data={data}
            isLoading={isLoading}
            onViewAll={() => navigate(`/invoicing?tab=paid&${invoicingDateBasisQuery}`)}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
          <DashboardAnalyticsRevenuePanel
            data={data}
            isLoading={isLoading}
            onViewInvoices={() => navigate(`/invoicing?tab=due&${invoicingDateBasisQuery}`)}
          />
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} useFlexGap sx={{ alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DashboardAnalyticsClientActivityCard
            data={data}
            isLoading={isLoading}
            isCustomDateRange={isCustomDateRange}
            onViewAllClients={() => navigate('/clients')}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DashboardAnalyticsQuestionnaireCard
            items={data?.questionnaireCompletion.items}
            isLoading={isLoading}
            onViewAll={() => navigate('/questionnaires')}
          />
        </Box>
      </Stack>
    </Stack>
  );
};
